import { createRef, useEffect, useRef, useState } from 'react';
import type { MouseEvent, RefObject } from 'react';

import IconTriangleDown from 'components/IconTriangleDown';

export type TabsProps = {
  data: { title: string; content: JSX.Element | string }[];
};

const Tabs = ({ data }: TabsProps) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const elRefs = useRef<RefObject<HTMLDivElement>[]>(data.map(() => createRef()));
  const shouldScrollRef = useRef<boolean>();

  useEffect(() => {
    if (shouldScrollRef.current === true) {
      const ref = elRefs.current[activeTab];

      if (ref && ref?.current?.offsetTop) {
        window.scrollTo({
          top: Math.floor(ref.current.offsetTop),
          behavior: 'smooth',
        });

        shouldScrollRef.current = false;
      }
    }
  }, [activeTab]);

  const handleTabChange = (tabIndex: number, shouldScroll?: boolean) => (ev: MouseEvent) => {
    ev.preventDefault();

    if (shouldScroll) {
      shouldScrollRef.current = true;
    }

    setActiveTab(tabIndex);
  };

  return (
    <div className="grid grid-cols-12">
      <div className="col-span-3" role="tablist">
        <div className="my-auto w-full md:border-r-1 md:border-gold">
          {data.map(({ title }, i) => {
            const isActive = activeTab === i;

            return (
              <a
                className={`mb-16 hidden p-16 font-semibold text-black md:block${isActive ? ' bg-gold' : ''}`}
                href="#"
                key={`tab-${i}`}
                onClick={handleTabChange(i)}
                role="tab"
              >
                {title}
              </a>
            );
          })}
        </div>
      </div>

      <div className="col-span-12 grid gap-16 md:col-span-9  md:pl-32" role="tablist">
        {data.map(({ content, title }, i) => {
          const isActive = activeTab === i;

          return (
            <div aria-expanded={isActive ? 'true' : 'false'} key={`tab-panel-${i}`} ref={elRefs.current[i]} role="tabpanel">
              <a
                className={`relative mb-16 block border-2 border-gold p-16 md:hidden${isActive ? ' bg-gold' : ''}`}
                href="#"
                onClick={handleTabChange(i, true)}
                role="tab"
              >
                <IconTriangleDown className={`absolute h-24 w-24 ${isActive ? '' : ' -rotate-90'}`} />
                <div className="w-full shrink-0 text-center font-semibold text-black">{title}</div>
              </a>
              <div className={isActive ? '' : 'hidden'}>{content}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Tabs;
